import prepareTranslate from '@/helpers/dictionary';
import { useForm } from '@mantine/form';
import {
  Button,
  Grid,
  MultiSelect,
  NumberInput,
  Select,
  TextInput,
  Textarea,
} from '@mantine/core';
import { useState } from 'react';
import { ACCORD_TYPE, PRODUCT_ENTITY } from '@/helpers/enums';
import DetailsModal from '@/components/DetailsModal';
import companyQuery from '@/api/queries/company';
import productQuery from '@/api/queries/product';
import validations from '@/helpers/validation';
import { MonthPickerInput } from '@mantine/dates';
import agentMutate from '@/api/mutitations/agent';
import s3Mutations from '@/api/mutitations/s3';
import Modal from '@/components/Modal/Index';
import { useDisclosure } from '@mantine/hooks';
import PropTypes from 'prop-types';
import UploadFilesButton from '../uploadFileButton';
import EditFiles from '../EditFiles';
import TieredCommissionInput from '../TieredCommissionInput';

function AgentAgreementCreate(props) {
  const {
    open,
    close,
    opened,
    setKey,
    id,
  } = props;

  const t = prepareTranslate();
  const { useUpload } = s3Mutations;
  const [actionResultOpended,
    { open: actionResultOpen, close: actionResultClose },
  ] = useDisclosure();

  const { mutateAsync: uploadMutate } = useUpload();

  const { data: companiesData } = companyQuery.useGetAll();
  const { data: productsData } = productQuery.useGetAll();
  const [loading, setLoading] = useState(false);
  const [resultLabel, setResultLabel] = useState('');

  const form = useForm({
    initialValues: {
      accordType: 'independent',
      files: [],
      comments: '',
      agentFeeAnnualDeposit: [{ from: 0, to: null, rate: 0 }],
      agentFeeAnnualAccumulation: [{ from: 0, to: null, rate: 0 }],
    },
    validate: {
      agentNumber: (value) => validations.required(value),
      companyId: (value) => validations.required(value),
      branchId: (value) => validations.required(value),
      accordType: (value) => validations.required(value),
      depositFrom: (value) => {
        const hasDeposit = form.values.agentFeeMonthlyDeposit > 0
          || form.values.agentFeeMonthlyAccumulation > 0;
        return hasDeposit ? validations.required(value) : null;
      },
      accumulationFrom: (value) => {
        const hasMeaningfulTier = (tiers) => Array.isArray(tiers)
          && tiers.some((tier) => tier.rate > 0 || tier.to !== null);
        const hasAccumulation = hasMeaningfulTier(form.values.agentFeeAnnualDeposit)
          || hasMeaningfulTier(form.values.agentFeeAnnualAccumulation);
        return hasAccumulation ? validations.required(value) : null;
      },
      agentFeeAnnualDeposit: (tiers) => {
        if (!Array.isArray(tiers) || tiers.length <= 1) return null;
        for (let i = 1; i < tiers.length; i += 1) {
          if ((tiers[i]?.rate ?? 0) <= (tiers[i - 1]?.rate ?? 0)) {
            return t('components.tieredCommissionInput.rateError');
          }
        }
        return null;
      },
      agentFeeAnnualAccumulation: (tiers) => {
        if (!Array.isArray(tiers) || tiers.length <= 1) return null;
        for (let i = 1; i < tiers.length; i += 1) {
          if ((tiers[i]?.rate ?? 0) <= (tiers[i - 1]?.rate ?? 0)) {
            return t('components.tieredCommissionInput.rateError');
          }
        }
        return null;
      },
    },
  });

  const companiesOptions = companiesData ? (
    companiesData.map((company) => ({
      label: company.hebrew,
      value: company._id,
    }))) : (
    []
  );

  // eslint-disable-next-line max-len
  const productsOptions = productsData?.filter((product) => product.applicableTo.includes(PRODUCT_ENTITY.agreement)).map((product) => ({
    label: product.hebrew,
    value: product._id,
  }));

  const { mutateAsync: addAgreementMutate } = agentMutate.usePushToArray();

  const handleAgreementSave = async (values) => {
    setLoading(true);
    let uploadFiles;

    try {
      const clonedValues = { ...values };

      if (clonedValues.accordType === 'employee') {
        clonedValues.agentFeeMonthlyDeposit = 0;
        clonedValues.agentFeeMonthlyAccumulation = 0;
      } else if (clonedValues.accordType === 'independent') {
        clonedValues.employeeAgentCommission = 0;
      }

      const { files } = values;

      if (files.length > 0) {
        const formData = new FormData();

        files.forEach((file) => {
          const realFile = file.file;
          formData.append('files', realFile, file.name);
        });

        try {
          uploadFiles = await uploadMutate(formData);
          clonedValues.files = uploadFiles.files;
        } catch (error) {
          setResultLabel(t('pages.agentEdit.notification.failure.title'));
          actionResultOpen();
          return;
        }
      }

      const data = {
        value: { ...clonedValues },
        id,
        key: 'agreements',
      };

      const newAgreement = await addAgreementMutate(data);

      if (!newAgreement) {
        setResultLabel(t('pages.agentEdit.notification.failure.title'));
        actionResultOpen();
        return;
      }

      setResultLabel(t('components.createContracts.success'));
      actionResultOpen();
      form.reset();
      setKey((prev) => prev + 1);
      close();
    } catch (error) {
      setResultLabel(t('components.createContracts.failture'));
      actionResultOpen();
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = () => {
    form.reset();
    form.clearErrors();
    close();
  };

  const actions = [
    <Button
      key='primary'
      loading={loading}
      onClick={form.onSubmit(handleAgreementSave)}
      w={'7rem'}
    >
      {t('global.update')}
    </Button>,
    <Button
      key='cancel'
      variant='outline'
      onClick={handleCancelClick}
      w={'7rem'}>
      {t('global.cancel')}
    </Button>,
  ];

  const resultActions = [
    <Button
      key='clseButton' onClick={() => actionResultClose()}>
      {t('global.close')}
    </Button>,
  ];

  const onFileSelected = (files) => {
    const formattedFiles = files.map((file) => ({ file, name: file.name }));
    form.setFieldValue('files', [...form.values.files, ...formattedFiles]);
  };

  const onFileNameChange = (index, newName) => {
    const updated = [...form.values.files];
    updated[index] = { ...updated[index], name: newName };
    form.setFieldValue('files', updated);
  };

  const onDeleteFile = (index) => {
    form.removeListItem('files', index);
  };
  const hasDepositFees = form.values.agentFeeMonthlyDeposit > 0
    || form.values.agentFeeMonthlyAccumulation > 0 || form.values.employeeAgentCommission > 0;
  const hasMeaningfulTier = (tiers) => Array.isArray(tiers)
    && tiers.some((tier) => tier.rate > 0 || tier.to !== null);
  const hasAccumulationFees = hasMeaningfulTier(form.values.agentFeeAnnualDeposit)
    || hasMeaningfulTier(form.values.agentFeeAnnualAccumulation);
  const agreementAccordType = form.values.accordType;

  const depositUntilMinDate = form.values.depositFrom
    ? new Date(
      form.values.depositFrom.getFullYear(),
      form.values.depositFrom.getMonth() + 1,
      1,
    ) : undefined;

  const accumulationUntilMinDate = form.values.accumulationFrom
    ? new Date(
      form.values.accumulationFrom.getFullYear(),
      form.values.accumulationFrom.getMonth() + 1,
      1,
    ) : undefined;

  return (
    <>
      <DetailsModal
        actions={actions}
        open={open}
        close={handleCancelClick}
        opened={opened}
        label={t('components.addAgentAgreement.title')}
        size='90%'
      >
        <Grid gutter='lg'>
          <Grid.Col span={{ base: 3, md: 3 }}>
            <Select
              autoComplete='off'
              label={t('components.editContracts.company')}
              data={companiesOptions}
              required
              key={form.key('companyId')}
              maxLength={20}
              {...form.getInputProps('companyId')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 3, md: 3 }}>
            <TextInput
              autoComplete='off'
              required
              label={t('components.editContracts.agent')}
              key={form.key('agentNumber')}
              maxLength={10}
              {...form.getInputProps('agentNumber')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 3, md: 3 }}>
            <MultiSelect
              required
              label={t('components.editContracts.branch')}
              data={productsOptions}
              key={form.key('branchId')}
              {...form.getInputProps('branchId')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 3, md: 3 }}>
            <Select
              required
              label={t('components.editContracts.accordType')}
              data={ACCORD_TYPE}
              key={form.key('accordType')}
              {...form.getInputProps('accordType')}
            />
          </Grid.Col>

        </Grid>
        <Grid gutter='lg'>
          <Grid.Col span={{ base: 3, md: 3 }}>
            <MonthPickerInput
              required={hasDepositFees}
              maxDate={new Date()}
              label={t('components.addAgentAgreement.form.depositFrom')}
              key={form.key('depositFrom')}
              {...form.getInputProps('depositFrom')}
              onChange={(value) => {
                form.setFieldValue('depositFrom', value);
                if (value && form.values.depositUntil && value > form.values.depositUntil) {
                  form.setFieldValue('depositUntil', null);
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 3, md: 3 }}>
            <MonthPickerInput
              disabled={!form.values.depositFrom}
              minDate={depositUntilMinDate}
              label={t('components.addAgentAgreement.form.depositUntil')}
              key={form.key('depositUntil')}
              {...form.getInputProps('depositUntil')}
            />
          </Grid.Col>
          {agreementAccordType === 'independent' ? (
            <>
              <Grid.Col span={{ base: 3, md: 3 }}>
                <NumberInput
                  autoComplete='off'
                  label={t('components.editContracts.commision.agentFeeMonthlyDeposit')}
                  key={form.key('agentFeeMonthlyDeposit')}
                  maxLength={5}
                  min={0}
                  defaultValue={0}
                  {...form.getInputProps('agentFeeMonthlyDeposit')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 3, md: 3 }}>
                <NumberInput
                  autoComplete='off'
                  label={t('components.editContracts.commision.agentFeeMonthlyAccumulation')}
                  key={form.key('agentFeeMonthlyAccumulation')}
                  maxLength={5}
                  min={0}
                  defaultValue={0}
                  {...form.getInputProps('agentFeeMonthlyAccumulation')}
                />
              </Grid.Col>
            </>
          ) : (
            <>
              <Grid.Col span={{ base: 3, md: 3 }}>
                <NumberInput
                  autoComplete='off'
                  label={t('components.editContracts.commision.employeeAgentCommission')}
                  key={form.key('employeeAgentCommission')}
                  maxLength={5}
                  min={0}
                  defaultValue={0}
                  {...form.getInputProps('employeeAgentCommission')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 3, md: 3 }}>
                <TextInput
                  label={t('components.editContracts.accordName')}
                  key={form.key('accordName')}
                  maxLength={200}
                  {...form.getInputProps('accordName')}
                />
              </Grid.Col>
            </>
          )}
        </Grid>

        <Grid gutter='lg'>
          <Grid.Col span={{ base: 3, md: 3 }}>
            <MonthPickerInput
              required={hasAccumulationFees}
              maxDate={new Date()}
              label={t('components.addAgentAgreement.form.accumulationFrom')}
              key={form.key('accumulationFrom')}
              {...form.getInputProps('accumulationFrom')}
              onChange={(value) => {
                form.setFieldValue('accumulationFrom', value);
                // eslint-disable-next-line max-len
                if (value && form.values.accumulationUntil && value > form.values.accumulationUntil) {
                  form.setFieldValue('accumulationUntil', null);
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 3, md: 3 }}>
            <MonthPickerInput
              disabled={!form.values.accumulationFrom}
              minDate={accumulationUntilMinDate}
              label={t('components.addAgentAgreement.form.accumulationUntil')}
              key={form.key('accumulationUntil')}
              {...form.getInputProps('accumulationUntil')}
            />
          </Grid.Col>
          {agreementAccordType === 'independent' && (
            <Grid.Col span={{ base: 3, md: 3 }}>
              <TextInput
                label={t('components.editContracts.accordName')}
                key={form.key('accordName')}
                maxLength={200}
                {...form.getInputProps('accordName')}
              />
            </Grid.Col>
          )}
          <Grid.Col span={12}>
            <TieredCommissionInput
              label={t('components.editContracts.commision.agentFeeAnnualDeposit')}
              {...form.getInputProps('agentFeeAnnualDeposit')}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <TieredCommissionInput
              label={t('components.editContracts.commision.agentFeeAnnualAccumulation')}
              {...form.getInputProps('agentFeeAnnualAccumulation')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 3, md: 3 }}>
            <Textarea
              label={t('components.editContracts.comments')}
              key={form.key('comments')}
              maxLength={500}
              minRows={3}
              {...form.getInputProps('comments')}
            />
          </Grid.Col>
        </Grid>
        <UploadFilesButton
          onFilesSelected={onFileSelected}
          label={t('pages.agencyCommissionsCreate.form.uploadFiles')}
        />

        {form.values.files?.length > 0 && (
          <EditFiles
            files={form.values.files}
            onNameChange={onFileNameChange}
            onRemove={onDeleteFile}
          />
        )}
      </DetailsModal>
      <Modal
        opened={actionResultOpended}
        onClose={actionResultClose}
        actions={resultActions}
        label={resultLabel}
      />
    </>
  );
}

AgentAgreementCreate.propTypes = {
  open: PropTypes.func,
  close: PropTypes.func,
  opened: PropTypes.bool,
  setKey: PropTypes.func,
  id: PropTypes.string,
};

export default AgentAgreementCreate;
